import type { Map as MlMap } from 'maplibre-gl'

/**
 * Infrastructure badges: one glyph per geofence type.
 *
 * A polygon tells you where something is and nothing about what it is. On this corridor
 * that matters more than usual, because the twenty-five fences are not variations on a
 * theme -- a toll plaza, a ghat, a bridge, a plant yard and a gate queue behave completely
 * differently, produce different alerts, and are read by different people. Rendering them
 * as twenty-five tinted blobs throws that away and forces a click to recover it.
 *
 * SHAPE CARRIES CLASS, GLYPH CARRIES IDENTITY, COLOUR CARRIES NEITHER.
 *
 *   square    a place trucks stop at        plant, depot, yard, rest
 *   shield    a place trucks pass through   gate, queue, toll
 *   triangle  a place the road does         ghat, bridge
 *   diamond   a place something happens     activity, congestion
 *
 * That means the map is still readable at a glance in greyscale, on a projector with the
 * contrast wrong, and to the ~8% of male operators with a red-green deficiency. Colour is
 * a redundant third channel here, never the only one -- the same rule the vehicle markers
 * follow, where load is a fill and not a hue.
 *
 * Drawn to a canvas at device pixel ratio rather than shipped as a sprite sheet: the
 * basemap sprite belongs to protomaps and is not a contract, and `icon-color` only tints
 * SDF images, which these are not.
 */

const S = 34               // badge box, CSS px
const R = 8                // corner radius on the square form

export type FenceShape = 'square' | 'shield' | 'triangle' | 'diamond'

export interface FenceStyle {
  shape: FenceShape
  color: string
  label: string
  /** What an operator should take from seeing it, one line, for the legend. */
  hint: string
}

/**
 * The registry. Types absent here fall through to a neutral diamond rather than being
 * dropped -- an unknown fence is still a fence, and silently not drawing it is how a new
 * geofence from the Earth project goes unnoticed for a month.
 */
export const FENCE_STYLE: Record<string, FenceStyle> = {
  plant:      { shape: 'square',   color: '#7C3AED', label: 'Plant',      hint: 'loading, sets LADEN' },
  depot:      { shape: 'square',   color: '#0369A1', label: 'Depot',      hint: 'home base, charging' },
  yard:       { shape: 'square',   color: '#047857', label: 'Yard',       hint: 'marshalling, dwell' },
  rest:       { shape: 'square',   color: '#0D9488', label: 'Rest stop',  hint: 'driver break' },
  gate:       { shape: 'shield',   color: '#B45309', label: 'Gate',       hint: 'checkpoint, must be crossed' },
  queue:      { shape: 'shield',   color: '#C2410C', label: 'Queue',      hint: 'approach lane, dwell is normal' },
  toll:       { shape: 'shield',   color: '#9333EA', label: 'Toll',       hint: 'plaza, congestion counts here' },
  ghat:       { shape: 'triangle', color: '#7C2D12', label: 'Ghat',       hint: 'gradient, descent risk' },
  bridge:     { shape: 'triangle', color: '#475569', label: 'Bridge',     hint: 'weight-limited span' },
  activity:   { shape: 'diamond',  color: '#4F46E5', label: 'Activity',   hint: 'operational zone' },
  congestion: { shape: 'diamond',  color: '#9F1239', label: 'Congestion', hint: 'known slow section' },
}

const FALLBACK: FenceStyle = {
  shape: 'diamond', color: '#64748B', label: 'Zone', hint: 'unclassified geofence',
}

export function fenceStyle(type: string | null | undefined): FenceStyle {
  return FENCE_STYLE[String(type ?? '').toLowerCase()] ?? FALLBACK
}

export function fenceIconId(type: string | null | undefined): string {
  const t = String(type ?? '').toLowerCase()
  return `fence-${t in FENCE_STYLE ? t : 'other'}`
}

// ------------------------------------------------------------------ badge shapes
function badgePath(g: CanvasRenderingContext2D, shape: FenceShape): void {
  const c = S / 2
  g.beginPath()
  switch (shape) {
    case 'square': {
      const x = 4, y = 4, w = S - 8, h = S - 8
      g.moveTo(x + R, y)
      g.arcTo(x + w, y, x + w, y + h, R)
      g.arcTo(x + w, y + h, x, y + h, R)
      g.arcTo(x, y + h, x, y, R)
      g.arcTo(x, y, x + w, y, R)
      g.closePath()
      break
    }
    case 'shield': {
      // Flat shoulders, tapered foot: a road sign, and the foot points at the ground it
      // refers to, which a plain square cannot do.
      g.moveTo(c - 11, 5)
      g.lineTo(c + 11, 5)
      g.lineTo(c + 11, S - 15)
      g.quadraticCurveTo(c + 11, S - 7, c, S - 3)
      g.quadraticCurveTo(c - 11, S - 7, c - 11, S - 15)
      g.closePath()
      break
    }
    case 'triangle': {
      g.moveTo(c, 4)
      g.lineTo(S - 4, S - 6)
      g.lineTo(4, S - 6)
      g.closePath()
      break
    }
    case 'diamond': {
      g.moveTo(c, 3)
      g.lineTo(S - 3, c)
      g.lineTo(c, S - 3)
      g.lineTo(3, c)
      g.closePath()
      break
    }
  }
}

// ------------------------------------------------------------------ glyphs
/** All glyphs draw in white, centred, inside a ~14 px box. Stroke, not fill, unless noted. */
const GLYPH: Record<string, (g: CanvasRenderingContext2D) => void> = {
  // Chimneys over a shed: the universal works/factory mark.
  plant(g) {
    const c = S / 2
    g.fillRect(c - 7, c + 1, 14, 6)
    g.fillRect(c - 5, c - 4, 3, 5)
    g.fillRect(c - 0.5, c - 7, 3, 8)
    g.fillRect(c + 4, c - 5, 3, 6)
  },
  // A roof over an opening: shelter, i.e. home base.
  depot(g) {
    const c = S / 2
    g.beginPath()
    g.moveTo(c - 8, c); g.lineTo(c, c - 7); g.lineTo(c + 8, c)
    g.stroke()
    g.fillRect(c - 6, c, 12, 7)
  },
  // Bays side by side, seen from above: marshalling.
  yard(g) {
    const c = S / 2
    for (let i = -1; i <= 1; i++) g.fillRect(c - 7 + (i + 1) * 5, c - 6, 3, 12)
  },
  // Cup: a break, not a stop.
  rest(g) {
    const c = S / 2
    g.beginPath()
    g.moveTo(c - 5, c - 4); g.lineTo(c - 4, c + 5); g.lineTo(c + 3, c + 5); g.lineTo(c + 4, c - 4)
    g.closePath(); g.stroke()
    g.beginPath(); g.arc(c + 5, c - 0.5, 3, -1.2, 1.2); g.stroke()
  },
  // A lifted barrier arm on its post: a checkpoint you go through.
  gate(g) {
    const c = S / 2
    g.fillRect(c - 8, c - 6, 3, 13)
    g.save(); g.translate(c - 6.5, c - 4); g.rotate(-0.5)
    g.fillRect(0, -1.5, 15, 3)
    g.restore()
  },
  // Stacked chevrons: things waiting, in order.
  queue(g) {
    const c = S / 2
    g.lineWidth = 2
    for (let i = 0; i < 3; i++) {
      const y = c - 6 + i * 5.5
      g.beginPath(); g.moveTo(c - 5, y); g.lineTo(c, y + 3.5); g.lineTo(c + 5, y); g.stroke()
    }
  },
  // Booth with an arm across: the plaza.
  toll(g) {
    const c = S / 2
    g.fillRect(c - 7, c - 6, 6, 12)
    g.fillRect(c - 1, c - 1, 12, 3)
  },
  // Two peaks: a climb and a descent, which is what a ghat costs a laden truck.
  ghat(g) {
    const c = S / 2
    g.lineWidth = 2
    g.beginPath()
    g.moveTo(c - 9, c + 6); g.lineTo(c - 3, c - 3); g.lineTo(c + 1, c + 2)
    g.lineTo(c + 5, c - 5); g.lineTo(c + 9, c + 6)
    g.stroke()
  },
  // A deck on two piers: a span, and one with a limit worth respecting.
  bridge(g) {
    const c = S / 2
    g.fillRect(c - 9, c, 18, 2.5)
    g.fillRect(c - 6, c + 2, 2.5, 6)
    g.fillRect(c + 3.5, c + 2, 2.5, 6)
    g.lineWidth = 2
    g.beginPath(); g.moveTo(c - 9, c); g.quadraticCurveTo(c, c - 10, c + 9, c); g.stroke()
  },
  // Concentric: something is going on here.
  activity(g) {
    const c = S / 2
    g.lineWidth = 2
    g.beginPath(); g.arc(c, c, 6, 0, Math.PI * 2); g.stroke()
    g.beginPath(); g.arc(c, c, 2, 0, Math.PI * 2); g.fill()
  },
  // Bang: the only glyph allowed to be an exclamation, so it keeps its force.
  congestion(g) {
    const c = S / 2
    g.fillRect(c - 1.5, c - 7, 3, 8)
    g.beginPath(); g.arc(c, c + 5, 1.8, 0, Math.PI * 2); g.fill()
  },
  other(g) {
    const c = S / 2
    g.beginPath(); g.arc(c, c, 4, 0, Math.PI * 2); g.fill()
  },
}

function badge(style: FenceStyle, key: string, ratio: number): ImageData {
  const cv = document.createElement('canvas')
  cv.width = S * ratio
  cv.height = S * ratio
  const g = cv.getContext('2d')!
  g.scale(ratio, ratio)
  g.lineJoin = 'round'
  g.lineCap = 'round'

  // The badge sits on a live map, so it needs to survive both a pale field and a dark
  // satellite tile. A white ring does that in one stroke where a shadow would not.
  badgePath(g, style.shape)
  g.fillStyle = style.color
  g.fill()
  g.strokeStyle = 'rgba(255,255,255,0.94)'
  g.lineWidth = 2
  g.stroke()

  g.fillStyle = '#FFFFFF'
  g.strokeStyle = '#FFFFFF'
  g.lineWidth = 2
  // Triangles put their glyph low, where the shape actually has room for it.
  if (style.shape === 'triangle') g.translate(0, 3)
  ;(GLYPH[key] ?? GLYPH.other!)(g)

  return g.getImageData(0, 0, S * ratio, S * ratio)
}

/** Idempotent: a style reload re-runs this, and addImage throws on a duplicate id. */
export function registerFenceIcons(map: MlMap): void {
  const ratio = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)))
  const entries: [string, FenceStyle][] = [
    ...Object.entries(FENCE_STYLE), ['other', FALLBACK],
  ]
  for (const [key, style] of entries) {
    const id = `fence-${key}`
    if (map.hasImage(id)) continue
    map.addImage(id, badge(style, key, ratio), { pixelRatio: ratio })
  }
}

/** MapLibre `match` over the fence type, yielding the badge image id. */
export function fenceIconExpr(): unknown {
  const pairs = Object.keys(FENCE_STYLE).flatMap((k) => [k, `fence-${k}`])
  return ['match', ['downcase', ['coalesce', ['get', 'type'], '']], ...pairs, 'fence-other']
}

/** MapLibre `match` over the fence type, yielding the badge colour. */
export function fenceColorExpr(): unknown {
  const pairs = Object.entries(FENCE_STYLE).flatMap(([k, v]) => [k, v.color])
  return ['match', ['downcase', ['coalesce', ['get', 'type'], '']], ...pairs, FALLBACK.color]
}
